import React, { useEffect, useState } from "react";
import api from "../../services/api";

const PhotosToReview = ({ onError }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState({}); // per-photo spinner

  // --- IG quota state for the Principal account ---
  const [igQuota, setIgQuota] = useState({
    usage: null,
    total: null,
    remaining: null,
    loading: false,
    error: null,
  });

  const load = async () => {
    try {
      const { data } = await api.get("/photos/photos-to-review");
      setItems(data);
    } catch (e) {
      onError?.("Failed to load photos to review.");
    }
  };

  // --- load current IG quota ---
  const loadQuota = async () => {
    setIgQuota((q) => ({ ...q, loading: true, error: null }));
    try {
      const { data } = await api.get("/auth/instagram/content-publishing-limit");
      setIgQuota({
        usage: data.quota_usage,
        total: data.quota_total,
        remaining: data.remaining,
        loading: false,
        error: null,
      });
    } catch (e) {
      setIgQuota((q) => ({
        ...q,
        loading: false,
        error:
          e?.response?.data?.detail ||
          "Could not read Instagram quota right now.",
      }));
    }
  };

  useEffect(() => {
    load();
    loadQuota(); // prefetch quota once screen opens
  }, []);

  const review = async (photoId, decision) => {
    let formattedDecision = decision;
    let postToInstagram = false;
    let notify = decision === "REJECTED";
    if (decision === "APPROVE_AND_POST") {
      formattedDecision = "APPROVED";
      postToInstagram = true;
    }
    setBusy((b) => ({ ...b, [photoId]: true }));
    try {
      const res = await api.post(
        `/photos/review-photo?photo_id=${photoId}&decision=${formattedDecision}&post_to_instagram=${postToInstagram}&notify=${notify}`
      );

      // Remove the item from the list
      setItems((prev) => prev.filter((p) => p.id !== photoId));

      // If we posted to Instagram successfully, decrement our local remaining count.
      if (postToInstagram && res?.data?.posted_to_instagram) {
        setIgQuota((q) => {
          if (q.remaining === null) return q; // no quota info
          const remaining = Math.max(0, (q.remaining || 0) - 1);
          const usage =
            q.usage == null || q.total == null
              ? q.usage
              : Math.min(q.total, (q.usage || 0) + 1);
          return { ...q, remaining, usage };
        });
      }
    } catch (e) {
      // --- SPECIFIC 429 HANDLING ---
      if (e?.response?.status === 429) {
        onError?.(
          "You’ve reached today’s Instagram publishing limit for this account. Try again tomorrow."
        );
      } else {
        const msg =
          e.response?.data?.detail ||
          "Failed to apply decision. If Instagram token expired, please reconnect in Profile.";
        onError?.(msg);
      }
    } finally {
      setBusy((b) => ({ ...b, [photoId]: false }));
    }
  };

  if (!items.length) {
    return (
      <div className="photos-content">
        <QuotaBanner igQuota={igQuota} onRefresh={loadQuota} />
        <p>No photos to review</p>
      </div>
    );
  }

  const approveAndPostDisabled =
    igQuota.remaining === 0 ||
    (typeof igQuota.error === "string" &&
      igQuota.error.toLowerCase().includes("not connected"));

  return (
    <div className="photos-content">
      <QuotaBanner igQuota={igQuota} onRefresh={loadQuota} />

      <ul>
        {items.map((photo) => (
          <li key={photo.id} className="photo-item">
            {busy[photo.id] ? (
              <p>Loading...</p>
            ) : (
              <>
                <img
                  src={`https://ghrianghraffy.com/${photo.thumbnail_path}`}
                  alt={photo.story || "Photo"}
                  style={{ maxWidth: 300 }}
                />
                <div className="photo-details">
                  <p>{photo.story || "No story"}</p>
                  <p>
                    <strong>Uploaded by:</strong> {photo.uploaded_by_name}
                  </p>
                </div>
                <div className="button-row" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button
                    onClick={() => review(photo.id, "APPROVE_AND_POST")}
                    disabled={approveAndPostDisabled}
                    title={
                      approveAndPostDisabled
                        ? "Instagram quota unavailable or reached"
                        : undefined
                    }
                  >
                    Approve &amp; Post
                  </button>

                  {/* --- Quota Chip right beside the Approve & Post button --- */}
                  <QuotaChip igQuota={igQuota} />

                  <button onClick={() => review(photo.id, "APPROVED")}>
                    Approve
                  </button>
                  <button
                    onClick={() => review(photo.id, "REJECTED")}
                    className="reject-button"
                  >
                    Reject
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// --- Quota chip component (tiny, inline, color-coded) ---
function QuotaChip({ igQuota }) {
  const { loading, remaining } = igQuota || {};

  const hasNumber = typeof remaining === "number";
  let bg = "#eef6ff";
  let color = "#0b4f9c";
  if (hasNumber && remaining === 0) {
    bg = "#ffe6e6"; // red-ish
    color = "#b00020";
  } else if (hasNumber && remaining <= 3) {
    bg = "#fff4e5"; // amber-ish
    color = "#8a4b00";
  }

  const label = loading
    ? "Checking…"
    : hasNumber
      ? `Remaining: ${remaining}`
      : "Remaining: —";

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 10px",
        borderRadius: 999,
        background: bg,
        color,
        fontSize: 12,
        lineHeight: 1.6,
        userSelect: "none",
        border: "1px solid rgba(0,0,0,0.06)",
      }}
      title={
        hasNumber
          ? `Instagram posts remaining in the rolling 24h window: ${remaining}`
          : "Instagram quota unavailable"
      }
    >
      {label}
    </span>
  );
}

// --- Small banner component to show quota status (top of page) ---
function QuotaBanner({ igQuota, onRefresh }) {
  const { usage, total, remaining, loading, error } = igQuota || {};
  const canShowNumbers =
    typeof usage === "number" &&
    typeof total === "number" &&
    typeof remaining === "number";

  return (
    <div
      style={{
        background: "#f8f9fb",
        border: "1px solid #e4e7ec",
        padding: "10px 12px",
        borderRadius: 8,
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div style={{ flex: 1 }}>
        <strong>Instagram Publishing Quota</strong>
        <div style={{ fontSize: 13, marginTop: 4 }}>
          {loading && <span>Checking quota…</span>}
          {!loading && canShowNumbers && (
            <span>
              Used <strong>{usage}</strong> of <strong>{total}</strong> in the last 24h
              &nbsp;|&nbsp; Remaining: <strong>{remaining}</strong>
            </span>
          )}
          {!loading && !canShowNumbers && (
            <span style={{ opacity: 0.8 }}>
              {error
                ? error
                : "Quota is unavailable right now. You can still approve photos (posting may be limited)."}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onRefresh}
        disabled={loading}
        style={{ fontSize: 12 }}
      >
        {loading ? "Refreshing…" : "Refresh"}
      </button>
    </div>
  );
}

export default PhotosToReview;
